/**
 * UKA-X01 Expert Clinical Review Portal
 * Content type body definitions for all 9 review content types.
 *
 * These are the typed shapes of ClinicalReviewItem.body — the actual
 * clinical content Dr. C reads and renders a verdict on.
 *
 * Each type corresponds to a section category in the 207-section review:
 *   formulary_supplement  — Tier 1 supplements (29 cards)
 *   formulary_rx          — Tier 2 Rx products (32 cards)
 *   formulary_longevity   — Tier 3 longevity interventions (13 cards)
 *   biomarker_range       — 3-tier biomarker ranges (109 cards)
 *   evidence_brief        — Off-label/controversial extended cards (8 cards)
 *   range_decision        — Controversial range justifications (3 cards)
 *   gap_question          — Open clinical questions (14 cards)
 *   methodology           — 3-tier range system methodology (1 card)
 *   attestation           — Master sign-off (1 card, unlocks after all others reviewed)
 */

// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-types
// ─────────────────────────────────────────────────────────────────────────────

export interface EvidenceReference {
  pmid: string | null;      // PubMed ID if available
  citation: string;         // Full citation string
  evidenceLevel: 'RCT' | 'meta-analysis' | 'cohort' | 'case-series' | 'animal' | 'expert-opinion';
  notes: string | null;
}

export interface SafetyRule {
  rule: string;
  severity: 'WARNING' | 'CONTRAINDICATION' | 'MONITOR';
  populations: string[];    // e.g. ["pregnancy", "renal-impairment"]
}

export interface DrugInteraction {
  drug: string;
  mechanism: string;
  clinicalSignificance: 'MAJOR' | 'MODERATE' | 'MINOR';
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. formulary_supplement — Tier 1 supplement product card
//    Source: src/data/formulary/seedSupplements.ts
//    Review: quick approval — "Approve All" batch available
// ─────────────────────────────────────────────────────────────────────────────

export interface FormularySupplementBody {
  contentTypeId: 'formulary_supplement';
  productId: string;
  name: string;
  indication: string;
  dosing: string;
  contraindications: string[];
  safetyRules: SafetyRule[];
  evidenceSummary: string;
  evidenceLevel: 'strong' | 'moderate' | 'weak' | 'insufficient';
  evidenceReferences: EvidenceReference[];
  targetBiomarkers: string[];     // Biomarker IDs this product targets
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. formulary_rx — Tier 2 prescription product card
//    Source: src/data/formulary/seedRx.ts
//    Off-label items flagged with offLabel=true on the parent ClinicalReviewItem
// ─────────────────────────────────────────────────────────────────────────────

export interface FormularyRxBody {
  contentTypeId: 'formulary_rx';
  productId: string;
  name: string;
  fdaIndication: string;
  offLabelUse: string | null;       // Populated when offLabel=true
  offLabelJustificationRequired: boolean;
  dosing: string;
  monitoringRequirements: string[];
  pregnancyCategory: string | null; // FDA category or "Avoid" / "Use with caution"
  drugInteractions: DrugInteraction[];
  contraindications: string[];
  safetyRules: SafetyRule[];
  scheduleClassification: string | null; // DEA schedule (e.g. "Schedule III")
  compoundingRequired: boolean;
  evidenceSummary: string;
  evidenceReferences: EvidenceReference[];
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. formulary_longevity — Tier 3 longevity intervention card
//    Source: src/data/formulary/seedLongevity.ts
//    Every Tier 3 item is flagged off-label — xPrize intervention candidates
// ─────────────────────────────────────────────────────────────────────────────

export interface FormularyLongevityBody {
  contentTypeId: 'formulary_longevity';
  productId: string;
  name: string;
  fdaStatus: 'approved' | 'off-label' | 'investigational' | 'not-approved';
  offLabelUse: string;              // Required for all Tier 3 items
  evidenceLevel: 'clinical-trial' | 'animal-only' | 'case-series' | 'expert-opinion' | 'none';
  evidenceReferences: EvidenceReference[];
  safetyRules: SafetyRule[];
  compoundingRequired: boolean;
  compoundingNotes: string | null;
  withinScopeOfCCMedicalPC: boolean | null;  // Dr. C must confirm this on approval
  xPrizeRelevance: string;          // Why this is an xPrize intervention candidate
  riskProfile: 'low' | 'moderate' | 'high' | 'investigational';
  contraindications: string[];
  monitoringRequirements: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. biomarker_range — 3-tier biomarker range card
//    Source: src/data/biomarkers/biomarkerCatalog.ts
//    Three-tier range system: standard / functional / xPrize
// ─────────────────────────────────────────────────────────────────────────────

export interface BiomarkerRangeValues {
  min: number;
  max: number;
  unit: string;
  notes: string | null;
}

export interface BiomarkerRangeBody {
  contentTypeId: 'biomarker_range';
  biomarkerId: string;
  biomarkerName: string;
  abbreviation: string;
  standardRange: BiomarkerRangeValues;
  functionalRange: BiomarkerRangeValues;
  xPrizeRange: BiomarkerRangeValues;
  currentValidationStatus: 'PLUG' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'NEEDS_UPDATE';
  rangeSanityCheckPassed: boolean;  // min < max, no negatives, unit consistency
  genderSpecific: boolean;
  genderSpecificNotes: string | null;
  ageRangeApplicability: string | null;
  sourceHierarchy: string;          // How this range was derived (Attia, Blueprint, NIA ITP, etc.)
  controversialFlag: boolean;       // True for PSA 1.5, cholesterol minimum, insulin upper limit
  controversialRationale: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. evidence_brief — Off-label / controversial extended card
//    8 items: Rapamycin, Oxandrolone+Nandrolone, PSA threshold,
//             LDN, Sermorelin, Dihexa, BPC-157, Methylene Blue
//    requiresJustification=true on parent ClinicalReviewItem
// ─────────────────────────────────────────────────────────────────────────────

export interface EvidenceBriefBody {
  contentTypeId: 'evidence_brief';
  briefId: string;                  // e.g. "brief-01-rapamycin"
  itemName: string;
  origin: string;                   // Provenance: who first proposed, year, mechanism
  evidenceFor: EvidenceReference[];
  honestCounterarguments: string[];
  netClinicalPosition: string;      // Platform's stated position — what Dr. C is approving
  annotationPrompt: string;         // Structured prompt guiding Dr. C's annotation
  riskTier: 'standard' | 'elevated' | 'high' | 'investigational-only';
  fdaStatus: 'approved' | 'off-label' | 'investigational' | 'not-approved';
  scheduleClassification: string | null;
  oncogenicPathwayConcern: boolean; // e.g. Dihexa — angiogenesis concern
  humanTrialData: boolean;          // false = animal data only (e.g. BPC-157)
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. range_decision — Controversial range justification card
//    3 items: PSA functional 1.5, total cholesterol min 180, fasting insulin 5.0
// ─────────────────────────────────────────────────────────────────────────────

export interface RangeDecisionBody {
  contentTypeId: 'range_decision';
  biomarkerId: string;
  biomarkerName: string;
  platformValue: number;            // What HemoLogix uses
  guidelineValue: number;           // What the mainstream guideline says
  unit: string;
  guidelineSource: string;          // e.g. "AUA 2023" for PSA 4.0
  deviationRationale: string;       // Clinical justification for the deviation
  riskOfDeviation: string;          // What could go wrong if platform is wrong
  supportingEvidence: EvidenceReference[];
  counterEvidence: EvidenceReference[];
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. gap_question — Open clinical question card
//    14 items from docs/dr-calapai-package-gaps.md
//    Note: uses ANSWERED semantics — not locked/approved, just answered
// ─────────────────────────────────────────────────────────────────────────────

export interface GapQuestionBody {
  contentTypeId: 'gap_question';
  gapId: string;                    // e.g. "gap-01-gender-cbc"
  question: string;
  clinicalContext: string;          // Background explaining why this gap matters
  currentPlatformBehavior: string;  // What the platform does today without the answer
  downstreamImpact: string;         // Where the answer feeds (C10, C04, Nurse, etc.)
  answerOptions: string[] | null;   // If multiple-choice (null = free text only)
  // Note: the reviewer's answer is captured in the Verdict.comment field,
  // not here. GapQuestion body is the question; Verdict is the answer.
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. methodology — 3-tier range system methodology card (1 card)
//    Source: specs/bones/captain/biomarker-research-prompt-v1.md
//    Dr. C confirms the methodology is sound and source hierarchy appropriate
// ─────────────────────────────────────────────────────────────────────────────

export interface MethodologyBody {
  contentTypeId: 'methodology';
  methodologyVersion: string;
  standardRangeSourceHierarchy: string[];   // e.g. ["LabCorp/Quest reference ranges", ...]
  functionalRangeSourceHierarchy: string[]; // e.g. ["functional medicine literature", ...]
  xPrizeRangeSourceHierarchy: string[];     // e.g. ["Bryan Johnson Blueprint", "NIA ITP", ...]
  rangeSystemDescription: string;           // How the 3-tier system was built
  dataValidationProcess: string;            // How ranges were validated before staging
  conflictResolutionProcess: string;        // How conflicts between sources were resolved
  knownLimitations: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. attestation — Master sign-off card (1 card)
//    Only available after all other 206 sections are reviewed.
//    Locks the entire review instance when approved.
// ─────────────────────────────────────────────────────────────────────────────

export interface AttestationBody {
  contentTypeId: 'attestation';
  totalSectionsForReview: number;   // Must equal the count of all other items
  attestationText: string;          // "I have reviewed all [N] sections..."
  reviewerName: string;             // "Dr. Christopher Calapai, D.O."
  reviewerCredentials: string;
  reviewerTitle: string;            // "Medical Director, CC Medical PC"
  tenantName: string;               // "HemoLogix" | "Firefighter" etc.
  reviewCycleId: string;            // Unique ID for this review cycle
  completionRequirement: 'all-sections-reviewed';  // Locked literal — cannot attest without all sections
}

// ─────────────────────────────────────────────────────────────────────────────
// 10. protocol_rule — Firefighter fork routing rules (Dr. Superko lane)
//     Not counted in the HemoLogix 207-section total.
//     Used when tenantId = "firefighter" for cardiovascular / occupational
//     exposure protocol routing rules.
// ─────────────────────────────────────────────────────────────────────────────

export interface ProtocolRuleBody {
  contentTypeId: 'protocol_rule';
  ruleId: string;
  trigger: string;           // Clinical condition or lab value that activates this rule
  action: string;            // Routing decision or protocol assignment
  rationale: string;         // Clinical justification for the rule
  contraindications: string[];
  evidenceReferences: EvidenceReference[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Discriminated union — the body field of ClinicalReviewItem
// ─────────────────────────────────────────────────────────────────────────────

export type ReviewItemBody =
  | FormularySupplementBody
  | FormularyRxBody
  | FormularyLongevityBody
  | BiomarkerRangeBody
  | EvidenceBriefBody
  | RangeDecisionBody
  | GapQuestionBody
  | MethodologyBody
  | AttestationBody
  | ProtocolRuleBody;

/**
 * Type guard helpers — use these when you need to narrow ReviewItemBody
 * to a specific content type after reading from Firestore.
 */
export function isFormularySupplementBody(b: ReviewItemBody): b is FormularySupplementBody {
  return b.contentTypeId === 'formulary_supplement';
}

export function isFormularyRxBody(b: ReviewItemBody): b is FormularyRxBody {
  return b.contentTypeId === 'formulary_rx';
}

export function isFormularyLongevityBody(b: ReviewItemBody): b is FormularyLongevityBody {
  return b.contentTypeId === 'formulary_longevity';
}

export function isBiomarkerRangeBody(b: ReviewItemBody): b is BiomarkerRangeBody {
  return b.contentTypeId === 'biomarker_range';
}

export function isEvidenceBriefBody(b: ReviewItemBody): b is EvidenceBriefBody {
  return b.contentTypeId === 'evidence_brief';
}

export function isRangeDecisionBody(b: ReviewItemBody): b is RangeDecisionBody {
  return b.contentTypeId === 'range_decision';
}

export function isGapQuestionBody(b: ReviewItemBody): b is GapQuestionBody {
  return b.contentTypeId === 'gap_question';
}

export function isMethodologyBody(b: ReviewItemBody): b is MethodologyBody {
  return b.contentTypeId === 'methodology';
}

export function isAttestationBody(b: ReviewItemBody): b is AttestationBody {
  return b.contentTypeId === 'attestation';
}

export function isProtocolRuleBody(b: ReviewItemBody): b is ProtocolRuleBody {
  return b.contentTypeId === 'protocol_rule';
}
